/**
 * Utility functions for document management
 */

/**
 * Downloads a document using the document ID
 * This approach avoids opening a new tab and ensures the file is downloaded directly
 * 
 * @param {string} documentId - The ID of the document to download
 * @param {string} filename - Optional filename for the downloaded file
 * @param {string} token - Authentication token
 */
export const downloadDocument = async (documentId, filename = 'document.docx', token) => {
  try {
    // Use fetch to get the document as a blob
    const response = await fetch(`/api/documents/${documentId}/download`, {
      headers: {
        'x-auth-token': token
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to download document');
    }
    
    // Get the filename from the content-disposition header if available
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    // Convert the response to a blob
    const blob = await response.blob();
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create an anchor element and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    
    // Add to the DOM, click to trigger download, then remove
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
};

/**
 * Direct download for specific file by name
 * 
 * @param {string} filename - The filename to download
 */
export const downloadFileByName = async (filename, token) => {
  try {
    // Use fetch to get the document as a blob
    const response = await fetch(`/api/documents/download-file/${filename}`, {
      headers: token ? {
        'x-auth-token': token
      } : {}
    });
    
    if (!response.ok) {
      throw new Error('Failed to download file');
    }
    
    // Convert the response to a blob
    const blob = await response.blob();
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create an anchor element and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    
    // Add to the DOM, click to trigger download, then remove
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};
